import { Fragment, useCallback, useEffect, useState } from "react";

import BoxContainer from "@hl/base-components/lib/BoxContainer";
import {
  Expand as ExpandIcon,
  Plus as PlusIcon,
  XCircleFill as DeleteIcon,
  Shuffle,
  PlusCircle,
  Check as CheckIcon,
  Minimize,
} from "@hl/base-components/lib/assets/icons.generated";
import ModalSection from "@hl/base-components/lib/modal/ModalSection";
import {
  BACKGROUND_COLOR,
  STATUS_COLOR,
  TEXT_COLOR,
} from "@hl/base-components/lib/theme/colors";
import { WEIGHT_BOLD } from "@hl/base-components/lib/theme/typography";
import { maskAddress } from "@hl/communities-app/lib/utils/content";
import {
  GenArtIFrame,
  GenProps,
} from "@hl/shared-features/lib/features/gen-art/GenArtIFrame";
import { useGenParams } from "@hl/shared-features/lib/features/gen-art/gen-art-utils";
import {
  Center,
  Modal,
  createStyles,
  Text,
  Title,
  Box,
  ActionIcon,
  Stack,
  Group,
  Divider,
  Button,
  ScrollArea,
  MantineTheme,
  Switch,
  Overlay,
  UnstyledButton,
  Flex,
  Textarea,
  Card,
  Space,
  Container,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import {
  useDisclosure,
  useListState,
  UseListStateHandlers,
} from "@mantine/hooks";
import { trim, uniq, difference } from "lodash";

import { useCreateCollectionFormContext } from "../context";

const useStyles = createStyles((theme) => ({
  plusBtn: {
    position: "absolute",
    bottom: 8,
    right: 8,
  },
  closeBtn: {
    position: "absolute",
    right: 10,
    top: 10,
    width: 36,
    height: 36,
    boxShadow: theme.shadows.sm,
    borderRadius: 6,
  },
  outputPanel: {
    height: 350,
    minHeight: 350,
    marginTop: 8,
    border: `1px solid ${theme.colors.divider[0]}`,
    borderRadius: 6,
  },
}));

const HashTile = ({
  mediaUrl,
  onAdd,
  onRemove,
  selectedHashes,
  params,
}: {
  mediaUrl: string;
  onAdd: (hash: string) => void;
  onRemove: (index: number) => void;
  selectedHashes: string[];
  params: Omit<GenProps, "mediaUrl">;
}) => {
  const { classes, theme } = useStyles();

  const selectedIndex = selectedHashes.findIndex((x) => x === params.txHash);
  const selected = selectedIndex !== -1;
  return (
    <UnstyledButton
      w="100%"
      onClick={() =>
        selected ? onRemove(selectedIndex) : onAdd(params.txHash)
      }
    >
      <Flex pos="relative">
        <GenArtIFrame mediaUrl={mediaUrl} pointerEvents="none" {...params} />
        <ActionIcon className={classes.plusBtn}>
          {selected ? (
            <CheckIcon color={theme.fn.themeColor(STATUS_COLOR.AFFIRMATIVE)} />
          ) : (
            <PlusIcon />
          )}
        </ActionIcon>
      </Flex>
    </UnstyledButton>
  );
};

export const HashPreview = ({
  mediaUrl,
  hash,
  onClose,
}: {
  mediaUrl: string;
  hash: string;
  onClose: () => void;
}) => {
  const { params } = useGenParams({ isCurated: true });
  const { classes, theme } = useStyles();

  return (
    <Box>
      <Center pos="relative" h="calc(100vh - 50px)">
        <GenArtIFrame mediaUrl={mediaUrl} {...params} txHash={hash} />

        <Card
          h={50}
          p={8}
          sx={{
            position: "absolute",
            bottom: 20,
            zIndex: 10,
            backgroundColor: theme.fn.themeColor(BACKGROUND_COLOR.PRIMARY),
            textAlign: "center",
          }}
        >
          <Text size="xs" fw={500}>
            Hash
          </Text>
          <Text size="xs">{hash}</Text>
        </Card>
      </Center>
      <ActionIcon className={classes.closeBtn} onClick={onClose}>
        <Minimize />
      </ActionIcon>
    </Box>
  );
};

const SelectedHashes = ({
  hashList,
  handleDelete,
  onPreview,
  overlayView,
}: {
  hashList: string[];
  handleDelete(index: number): void;
  onPreview?: (hash: string) => void;
  overlayView?: boolean;
}) => {
  const { classes } = useStyles();
  return (
    <ScrollArea
      sx={{ ...(overlayView && { minHeight: 500 }) }}
      className={classes.outputPanel}
      p="0 16px"
    >
      <Stack spacing={0} align="stretch">
        {hashList.map((hash, index) => (
          <Fragment key={hash}>
            <HashControl
              hash={hash}
              index={index}
              onDelete={handleDelete}
              onPreview={onPreview}
            />
            {index !== hashList.length - 1 && <Divider />}
          </Fragment>
        ))}
      </Stack>
    </ScrollArea>
  );
};

const useTileParams = () => {
  const isCurated = true;
  const param1 = useGenParams({ isCurated });
  const param2 = useGenParams({ isCurated });
  const param3 = useGenParams({ isCurated });
  const param4 = useGenParams({ isCurated });
  const randomizeAll = useCallback(() => {
    param1.randomizeAll();
    param2.randomizeAll();
    param3.randomizeAll();
    param4.randomizeAll();
  }, []);
  return {
    paramsList: [param1.params, param2.params, param3.params, param4.params],
    randomizeAll,
  };
};

export const useHashList = () => {
  const form = useCreateCollectionFormContext();

  const [hashList, listHandlers] = useListState<string>(
    form.values.generativeDetails?.allowedHashes ?? []
  );

  const handleDelete = useCallback(
    (index: number) => listHandlers.remove(index),
    [listHandlers]
  );
  const handleAdd = useCallback(
    (hash: string) => listHandlers.append(hash),
    [listHandlers]
  );

  const handleClear = useCallback(
    () => listHandlers.remove(0, hashList.length - 1),
    [listHandlers]
  );

  useEffect(() => {
    form.setFieldValue("generativeDetails.allowedHashes", hashList);
  }, [hashList]);

  return {
    hashList,
    handleDelete,
    handleAdd,
    handleClear,
    listHandlers,
  };
};

export const selectedHashesLabel = (selected: number, total?: number | null) =>
  `Selected hashes${total ? ` · ${selected}/${total}` : ""}`;

export const CurateOutputEditor = ({
  onClose,
  hashList,
  handleAdd,
  handleDelete,
  listHandlers,
}: {
  onClose: () => void;
  hashList: string[];
  handleDelete: (index: number) => void;
  handleAdd: (hash: string) => void;
  listHandlers: UseListStateHandlers<string>;
}) => {
  const { classes, theme } = useStyles();

  const form = useCreateCollectionFormContext();
  const mediaUrl = form.values.metadata.mediaUrl ?? "";

  const tileProps = {
    mediaUrl,
    onAdd: handleAdd,
    onRemove: handleDelete,
    selectedHashes: hashList,
  };
  const { paramsList, randomizeAll } = useTileParams();

  const [
    openedAddManually,
    { open: openAddManually, close: closeAddManually },
  ] = useDisclosure(false);

  const [previewHash, setPreviewHash] = useState<string | null>(null);
  const closeHashPreview = useCallback(() => {
    setPreviewHash(null);
  }, []);

  if (previewHash) {
    return (
      <HashPreview
        mediaUrl={mediaUrl}
        hash={previewHash}
        onClose={closeHashPreview}
      />
    );
  }

  return (
    <Overlay opacity={1}>
      <Box sx={{ borderBottom: `1px solid ${theme.colors.divider[0]}` }}>
        <Container size="lg">
          <Flex justify="space-between" align="center" py={10}>
            <Text fz={16} fw={500} align="center" w="100%">
              Curate output
            </Text>
            <Button onClick={onClose} color={BACKGROUND_COLOR.SECONDARY}>
              Done
            </Button>
          </Flex>
        </Container>
      </Box>
      <Container size="lg">
        <Flex>
          <Box mt={40} w="30%">
            <HashTile {...tileProps} params={paramsList[0]} />
            <Space h={40} />
            <HashTile {...tileProps} params={paramsList[1]} />
          </Box>
          <Box ml={40} mt={40} w="30%">
            <HashTile {...tileProps} params={paramsList[2]} />
            <Space h={40} />
            <HashTile {...tileProps} params={paramsList[3]} />
          </Box>
          <Box w="5%" />
          <Box
            w="35%"
            pt={40}
            pl={40}
            miw={400}
            sx={{ borderLeft: `1px solid ${theme.colors.divider[0]}` }}
          >
            <Text size="sm">
              Select hashes that will be used instead of the transaction hash.
            </Text>
            <Box>
              <Group position="apart" mt={40}>
                <Text size="sm" fw={WEIGHT_BOLD}>
                  {selectedHashesLabel(
                    hashList.length,
                    form.values.collection.size
                  )}
                </Text>
                <UnstyledButton onClick={openAddManually}>
                  <Group spacing={8} lh={1}>
                    <PlusCircle width={16} height={16} />
                    <Text size="xs" fw={WEIGHT_BOLD} lh={1}>
                      Add manually
                    </Text>
                  </Group>
                </UnstyledButton>
              </Group>
              {hashList.length === 0 && (
                <Center className={classes.outputPanel} mih={500}>
                  <Text color={TEXT_COLOR.PLACEHOLDER}>No hashes selected</Text>
                </Center>
              )}
              {hashList.length !== 0 && (
                <SelectedHashes
                  hashList={hashList}
                  handleDelete={handleDelete}
                  onPreview={setPreviewHash}
                  overlayView
                />
              )}
            </Box>
            <Button
              leftIcon={<Shuffle />}
              fullWidth
              onClick={randomizeAll}
              mt={40}
            >
              Generate more tokens
            </Button>
          </Box>
        </Flex>
      </Container>
      <AddHashesModal
        opened={openedAddManually}
        // Add only hashes that are not already in the list
        onAdd={(hashes) => listHandlers.append(...difference(hashes, hashList))}
        onClose={closeAddManually}
      />
    </Overlay>
  );
};

const HASH_REGEX = /^0x[a-fA-F0-9]{64}$/;

const AddHashesModal = ({
  opened,
  onClose,
  onAdd,
}: {
  opened: boolean;
  onClose: () => void;
  onAdd: (hashes: string[]) => void;
}) => {
  const parseHashes = (value: string) =>
    value.split(/[,|\n;]/g).map((hash) => trim(hash));

  const form = useForm<{ value: string }>({
    validateInputOnChange: true,
    initialValues: {
      value: "",
    },
    validate: {
      value: (value) => {
        const hashes = parseHashes(value);
        const invalid =
          !hashes.length || hashes.some((h) => !HASH_REGEX.test(h));

        if (invalid) {
          return "Enter a coma-separated list of 64-character hexadecimals prefixed with 0x";
        }
        return null;
      },
    },
  });

  const onCloseModal = () => {
    form.reset();
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={onCloseModal}
      title="Add hashes"
      zIndex={300}
      size="lg"
    >
      <form
        onSubmit={form.onSubmit((values) => {
          const newUniqueHashes = uniq(parseHashes(values.value));

          onAdd(newUniqueHashes);
          onCloseModal();
        })}
      >
        <Text>
          Enter the hashes you want to allow as inputs to your generative
          script. Use a comma-separated list of 64-character hexadecimals
          prefixed with 0x.
        </Text>

        <Textarea
          mt={16}
          label="Hashes"
          placeholder="0xe467956e5bd61518dc0354dc90c4bb2727a873fc83e28b4704a519c0e07b362a,0xe52c5d624d5617aadbb54b62212e011749217e3e85494c20acd9ba893ad993c3,0x3359bfbd47499d301032e48fe963607bf2b8706faf8dfc0bc58a6ea4d21e0266,0x9e29e561dc66d6afd052d3ddd0cda7d676071b848675764a0f67d09da2e7ea58"
          {...form.getInputProps("value")}
          rows={5}
          styles={{
            input: {
              minHeight: 120,
            },
          }}
        />
        <ModalSection>
          <Flex justify="space-between" mt={16}>
            <Button
              onClick={onCloseModal}
              color={BACKGROUND_COLOR.SECONDARY}
              fullWidth
            >
              Cancel
            </Button>
            <Button ml={16} type="submit" fullWidth disabled={!form.isValid()}>
              Add hashes
            </Button>
          </Flex>
        </ModalSection>
      </form>
    </Modal>
  );
};

const HashControl = ({
  hash,
  onDelete,
  index,
  onPreview,
}: {
  hash: string;
  onDelete: (index: number) => void;
  onPreview?: (hash: string) => void;
  index: number;
}) => (
  <Group align="center" position="apart" py={12}>
    <Text size="sm">{maskAddress(hash, 6, 16)}</Text>
    <Group spacing={4}>
      {onPreview && (
        <ActionIcon onClick={() => onPreview(hash)}>
          <ExpandIcon />
        </ActionIcon>
      )}
      <ActionIcon onClick={() => onDelete(index)}>
        <DeleteIcon />
      </ActionIcon>
    </Group>
  </Group>
);
const inputStyle = (theme: MantineTheme) => ({
  border: `1px solid ${theme.colors.divider[0]}`,
  borderRadius: "6px",
});

export const CurateOutputControl = ({
  onOpenEditor,
  onOpenHashPreview,
  disable,
  hashList,
  handleDelete,
  handleClear,
}: {
  onOpenEditor: () => void;
  onOpenHashPreview?: (hash: string) => void;
  disable: boolean;
  hashList: string[];
  handleDelete: (index: number) => void;
  handleClear: () => void;
}) => {
  const form = useCreateCollectionFormContext();
  const formValues = form.values;
  const [
    confirmRemoveOpened,
    { open: openConfirmRemove, close: closeConfirmRemove },
  ] = useDisclosure(false);
  const [learnMoreOpened, { open: openLearnMore, close: closeLearnMore }] =
    useDisclosure(false);
  const [
    setEditionSizeOpened,
    { open: openSetEditionSize, close: closeSetEditionSize },
  ] = useDisclosure(false);

  const discardOutput = () => {
    form.setFieldValue("generativeDetails.curateOutput", false);
    handleClear();
    closeConfirmRemove();
  };

  return (
    <Stack spacing={40}>
      <Box>
        <Text pb="xs" size="sm" component="label">
          Curate output
        </Text>
        <Text size="sm" mb={8}>
          Use a preselected set of hashes to seed randomness in your artwork.
          Enabling this option will cause hl.tx.hash to always be one of these
          preselected values instead of a transaction hash from the blockchain.
          If you do not use hl.tx.hash or any of the hl.random() functions from
          hl-gen.js, this option will have no effect on what your code outputs.{" "}
          <UnstyledButton onClick={openLearnMore}>
            <Text size="sm" fw={WEIGHT_BOLD}>
              Learn more
            </Text>
          </UnstyledButton>
        </Text>
        <Stack sx={inputStyle} spacing={0}>
          <Group px={16} position="apart" align="center" spacing={0} h={48}>
            {!disable && (
              <>
                <Text size="sm">
                  Use preselected hashes instead of transaction hashes
                </Text>
                <Switch
                  mt={4}
                  mr={4}
                  sx={{
                    input: {
                      height: "100%",
                    },
                  }}
                  {...form.getInputProps("generativeDetails.curateOutput", {
                    type: "checkbox",
                  })}
                  onChange={(event) => {
                    const checked = event.target.checked;
                    if (
                      !checked &&
                      (formValues.generativeDetails?.allowedHashes?.length ??
                        0) !== 0
                    ) {
                      openConfirmRemove();
                    } else {
                      form.setFieldValue(
                        "generativeDetails.curateOutput",
                        checked
                      );
                    }
                  }}
                />
              </>
            )}
            {disable && (
              <Text size="sm" opacity={0.5}>
                Use a limited series to enable curated output
              </Text>
            )}
          </Group>
        </Stack>
      </Box>
      {formValues.generativeDetails?.curateOutput && (
        <Stack spacing={8}>
          <Group position="apart">
            <Group>
              <Text size="sm" component="label">
                {selectedHashesLabel(
                  hashList.length,
                  form.values.collection.size
                )}
              </Text>
            </Group>
            {hashList.length !== 0 && (
              <UnstyledButton onClick={onOpenEditor}>
                <Text size="sm">Edit</Text>
              </UnstyledButton>
            )}
          </Group>
          {hashList.length !== 0 ? (
            <SelectedHashes
              hashList={hashList}
              handleDelete={handleDelete}
              onPreview={onOpenHashPreview}
            />
          ) : (
            <BoxContainer py={40}>
              <Stack spacing={0} align="center" ta="center">
                <Title order={6} fw={500}>
                  No hashes selected
                </Title>
                <Text mt={16} size="sm">
                  <div>
                    Select{" "}
                    {`${formValues.collection.size || ""} ${
                      !formValues.collection.size ||
                      formValues.collection.size > 1
                        ? "hashes"
                        : "hash"
                    }`}{" "}
                    that will be used to seed randomness in your artwork.
                  </div>
                  <div>
                    The number of hashes you select must equal the edition size.
                  </div>
                </Text>
                <Button
                  mt={32}
                  onClick={
                    formValues.collection.size
                      ? onOpenEditor
                      : openSetEditionSize
                  }
                  px={68}
                >
                  Select hashes
                </Button>
              </Stack>
            </BoxContainer>
          )}
        </Stack>
      )}

      <Modal
        opened={confirmRemoveOpened}
        onClose={closeConfirmRemove}
        title="Turn off curated outputs"
      >
        <Text>
          Turning off curated outputs will discard the outputs you have
          selected. Are you sure you want to do this?
        </Text>
        <Flex justify="space-between" mt={16}>
          <Button
            onClick={closeConfirmRemove}
            color={BACKGROUND_COLOR.SECONDARY}
            fullWidth
          >
            Cancel
          </Button>
          <Button ml={16} onClick={discardOutput} fullWidth>
            Discard selected outputs
          </Button>
        </Flex>
      </Modal>

      <Modal
        size="lg"
        opened={learnMoreOpened}
        onClose={closeLearnMore}
        title="Curate output"
      >
        <Text size="sm" component="p" mt={0}>
          On Highlight, you can curate what your code outputs by using a set of
          preselected hashes to seed randomness in your artwork (instead of
          transaction hashes from the blockchain). This allows you to have more
          granular control over what your code outputs. When someone mints your
          artwork, the hash will be randomly selected from that preselected
          list, ensuring fairness when minting.
        </Text>
        <Text size="sm" component="p">
          Specifically, if you choose to add preselected hashes, hl.tx.hash will
          give you a random hash from your preselected list as opposed to the
          blockchain transaction hash. Additionally, the hl.random() functions
          provided by hl-gen.js will be seeded by that preselected hash instead
          of the blockchain transaction hash and token ID.
        </Text>
      </Modal>
      <Modal
        size="lg"
        opened={setEditionSizeOpened}
        onClose={closeSetEditionSize}
        title="Set edition size"
      >
        <Text size="sm" component="p" mt={0}>
          Set the number of NFTs in this series before selecting hashes. The
          number of hashes you select must equal the edition size.
        </Text>
      </Modal>
    </Stack>
  );
};
